<!-- @format -->
<template>
    <div class="marketing-plan-template-overview-widget">
        <div class="widget-contents-container">
            <div class="widget-header">
                <div class="widget-title-container">
                    <j9-overview-svg />
                    <h3>Overview</h3>
                </div>
                <div class="svg-container widget-close-button" @click="closeWidget">
                    <widget-close-svg />
                </div>
            </div>
            <div class="content-wrapper">
                <div v-if="isLoading">
                    <loading-screen-svg />
                </div>
                <div v-else>
                    <div class="title-container">
                        <h3>{{ marketingPlanTemplate.name }}</h3>
                        <div
                            v-if="marketingPlanTemplate.isAgencyTemplate"
                            class="agency-logo-container"
                        >
                            By
                            <img
                                :src="marketingPlanTemplate.agencyLogoUrl | companyLogoPlaceholder"
                            />
                        </div>
                    </div>
                    <div v-if="marketingPlanTemplate.duration > 0" class="grey-bubble leads-bubble">
                        <div class="duration-container">
                            <q7-calendar-svg />
                            <div class="duration-content">
                                <p class="heavy-weight">Duration</p>
                                <p>{{ marketingPlanTemplate.duration }}</p>
                            </div>
                        </div>
                    </div>
                    <div class="description-container">
                        {{ marketingPlanTemplate.description }}
                    </div>
                    <div class="grey-bubble campaign-bubble">
                        <h4>
                            Campaigns in this Marketing Plan ({{
                                marketingPlanTemplate.campaignTemplates.length
                            }})
                        </h4>
                        <div class="preview-imgs-container">
                            <div
                                v-for="campaignTemplate in sortedCampaigns"
                                :key="campaignTemplate.id"
                                class="campaigns-container"
                            >
                                <div
                                    class="preview-img-container"
                                    @click="previewContent(campaignTemplate)"
                                >
                                    <div class="preview-overlay"><eye-svg /></div>
                                    <img
                                        class="preview-img"
                                        :src="campaignTemplate.previewImageUrl"
                                    />
                                </div>
                                <p class="send-date">Day {{ campaignTemplate.day }}</p>
                                <p class="campaign-name">{{ campaignTemplate.name }}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="widget-footer">
                <div class="cancel-container">
                    <app-button class="cancel-btn" @click="closeWidget"> Cancel </app-button>
                </div>
                <div class="action-container">
                    <app-button
                        v-if="agencyId"
                        class="edit-btn"
                        @click="openMarketingPlanTemplateWidget(marketingPlanTemplate)"
                    >
                        Edit Template
                    </app-button>
                    <app-button
                        v-if="clientId"
                        class="save-btn"
                        @click="openMarketingPlanWidget(marketingPlanTemplate)"
                    >
                        Create Marketing Plan
                    </app-button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
const AppButton = () => import('@/components/common/app-button');
const WidgetCloseSvg = () => import('@/components/svg/quick-add/widget-close-svg');
const LoadingScreenSvg = () => import('@/components/svg/loading-screen-svg');
const Q7CalendarSvg = () => import('@/components/svg/grid/q7-calendar-svg');
const J9OverviewSvg = () => import('@/components/svg/grid/j9-overview-svg');
const EyeSvg = () => import('@/components/svg/eye-svg');

import EventBus from '@/scripts/event-bus.js';

export default {
    name: 'marketing-plan-template-overview-widget',

    components: {
        'app-button': AppButton,
        'widget-close-svg': WidgetCloseSvg,
        'loading-screen-svg': LoadingScreenSvg,
        'q7-calendar-svg': Q7CalendarSvg,
        'j9-overview-svg': J9OverviewSvg,
        'eye-svg': EyeSvg,
    },

    props: {
        widget: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            isLoading: false,
            clientId: this.widget.clientId || null,
            agencyId: this.widget.agencyId || null,
            marketingPlanTemplate: {
                id: this.widget.marketingPlanTemplateId,
                name: null,
                duration: 0,
                description: null,
                campaignTemplates: [],
                agencyId: null,
                clientId: null,
                agencyLogoUrl: null,
            },

            templatePreviewImagePlaceholder:
                'https://cdn.honeycrm.com/app/Email-Template-Preview-Placeholder.png',
        };
    },

    computed: {
        sortedCampaigns() {
            return this.marketingPlanTemplate.campaignTemplates.sort((a, b) =>
                a.day > b.day ? 1 : -1,
            );
        },

        accountId() {
            return this.widget.accountId;
        },
    },

    created() {
        this.executeQuery();
        EventBus.$on('marketing-plan-template:update', this.executeQuery);
    },

    beforeDestroy() {
        EventBus.$off('marketing-plan-template:update', this.executeQuery);
    },

    methods: {
        executeQuery() {
            this.isLoading = true;

            var expandParam = '$expand=Agency($select=LogoImageUrl)';
            var selectParam =
                '$select=Id,Name,AgencyId,ClientId,Description,MarketingPlanTemplateCampaignTemplates';
            var filterParam = `$filter=Id eq ${this.marketingPlanTemplate.id}`;

            this.$api
                .get(`/service/MarketingPlanTemplates?${expandParam}&${filterParam}&${selectParam}`)
                .then((response) => {
                    var dataResponseItem = response.data.value[0];
                    var mptctResponse =
                        dataResponseItem.MarketingPlanTemplateCampaignTemplates || [];

                    var days = mptctResponse.map((mptct) => {
                        return parseInt(mptct.Day);
                    });

                    var duration = '';

                    if (days.length === 1) {
                        duration = '1 day';
                    } else if (days.length > 1) {
                        var end = Math.max.apply(null, days);
                        var start = Math.min.apply(null, days);
                        duration = end - start + 1 + ' days';
                    }

                    this.marketingPlanTemplate.name = dataResponseItem.Name;
                    this.marketingPlanTemplate.duration = `${duration}`;
                    this.marketingPlanTemplate.description = dataResponseItem.Description;
                    this.marketingPlanTemplate.agencyId = dataResponseItem.AgencyId;
                    this.marketingPlanTemplate.clientId = dataResponseItem.ClientId;
                    this.marketingPlanTemplate.agencyLogoUrl = dataResponseItem.Agency
                        ? dataResponseItem.Agency.LogoImageUrl
                        : null;
                })
                .then(this.getTemplates)
                .catch((error) => {
                    this.$helpers.showFailNotification('Failed to get Marketing Plan Template.', { error });
                })
                .finally(() => {
                    this.isLoading = false;
                });
        },

        getTemplates() {
            return new Promise((resolve, reject) => {
                var selectParam = '$select=CampaignTemplate,Day';
                var filterParam = `$filter=MarketingPlanTemplateId eq ${this.marketingPlanTemplate.id}`;
                var expandParam = '';

                if (this.marketingPlanTemplate.agencyId) {
                    expandParam = `$expand=CampaignTemplate($select=Id,Name,PreviewImageUrl)`;
                } else if (this.marketingPlanTemplate.clientId) {
                    expandParam = '$expand=CampaignTemplate($select=Id,Name,PreviewImageUrl)';
                } else {
                    expandParam = `$expand=CampaignTemplate($select=Id,Name,PreviewImageUrl)`;
                }

                var query = `/service/MarketingPlanTemplateCampaignTemplates?${expandParam}&${filterParam}&${selectParam}`;

                this.$api
                    .get(query)
                    .then((response) => {
                        this.marketingPlanTemplate.campaignTemplates = this.mapTemplates(
                            response.data.value,
                        );

                        resolve();
                    })
                    .catch(reject);
            });
        },

        mapTemplates(mptcts) {
            return mptcts.map((mptct) => {
                var previewImageUrl =
                    mptct.CampaignTemplate.PreviewImageUrl || this.templatePreviewImagePlaceholder;

                return {
                    id: mptct.CampaignTemplate.Id,
                    name: mptct.CampaignTemplate.Name,
                    day: mptct.Day,
                    previewImageUrl: previewImageUrl,
                };
            });
        },

        closeWidget() {
            EventBus.$emit('close-widget', this.widget);
        },

        previewContent(campaignTemplate) {
            if (this.clientId) {
                window.open(
                    `/Clients/${this.clientId}/CampaignTemplates/${campaignTemplate.id}/Preview`,
                    '_blank',
                );
            } else {
                window.open(`/CampaignTemplates/${campaignTemplate.id}/Preview`, '_blank');
            }
        },

        openMarketingPlanTemplateWidget(marketingPlanTemplate) {
            EventBus.$emit('open-widget', {
                name: 'marketing-plan-template-widget',
                agencyId: this.agencyId,
                accountId: this.accountId,
                marketingPlanTemplateId: marketingPlanTemplate.id,
            });
        },

        openMarketingPlanWidget(marketingPlanTemplate) {
            EventBus.$emit('open-widget', {
                name: 'marketing-plan-widget',
                marketingPlanTemplateId: marketingPlanTemplate.id,
                clientId: this.clientId,
                accountId: this.accountId,
            });

            this.closeWidget();
        },
    },
};
</script>

<style>
.marketing-plan-template-overview-widget .heavy-weight {
    font-weight: 600;
}

.marketing-plan-template-overview-widget .cancel-btn {
    background: transparent;
    color: #877d88;
    border: none;
}

.marketing-plan-template-overview-widget .cancel-btn:hover {
    background: #e9e9ef;
}

.marketing-plan-template-overview-widget .grey-bubble {
    background: #f7f7f8;
    border-radius: 15px;
    padding: 17px 28px 10px;
}

.marketing-plan-template-overview-widget .leads-bubble {
    display: flex;
    justify-content: flex-start;
}

.marketing-plan-template-overview-widget .leads-container,
.marketing-plan-template-overview-widget .duration-container,
.marketing-plan-template-overview-widget .title-container {
    display: flex;
}

.marketing-plan-template-overview-widget .title-container {
    justify-content: space-between;
    align-items: center;
    margin-bottom: 10px;
    padding: 0 20px;
}

.marketing-plan-template-overview-widget .title-container h3 {
    margin-bottom: 0;
    line-height: 1em;
}

.marketing-plan-template-overview-widget .preview-imgs-container {
    display: flex;
    flex-wrap: wrap;
}

.marketing-plan-template-overview-widget .average-leads-content p {
    color: #eb7e0a;
    font-size: 14px;
    line-height: 1em;
    margin-bottom: 7px;
}

.marketing-plan-template-overview-widget .duration-content p {
    font-size: 14px;
    line-height: 1em;
    text-align: left;
    margin-bottom: 3px;
}

.marketing-plan-template-overview-widget .j3-leads-svg {
    width: 60px;
    height: auto;
    margin-right: 15px;
}

.marketing-plan-template-overview-widget .grey-info-icon {
    width: 22px;
    margin-left: 15px;
    margin-top: -7px;
    position: relative;
}

.marketing-plan-template-overview-widget .grey-info-icon text {
    fill: #cac6cb;
}

.marketing-plan-template-overview-widget .grey-info-icon ellipse {
    stroke: #cac6cb;
}

.marketing-plan-template-overview-widget .description-container {
    font-size: 14px;
    padding: 17px;
}

.marketing-plan-template-overview-widget .q7-calendar-svg {
    margin-right: 15px;
}

.marketing-plan-template-overview-widget .preview-img-container {
    width: 93px;
    height: 63px;
    border-radius: 14px;
    overflow: hidden;
    position: relative;
    margin-bottom: 8px;
    cursor: pointer;
}

.marketing-plan-template-overview-widget .send-date {
    font-size: 11px;
    text-align: center;
    margin-bottom: 0px;
}

.marketing-plan-template-overview-widget .campaigns-container {
    text-align: center;
    margin-right: 20px;
    margin-bottom: 20px;
    max-width: 93px;
}

.marketing-plan-template-overview-widget .campaign-bubble h4 {
    margin-bottom: 15px;
}

.marketing-plan-template-overview-widget .preview {
    width: 160px;
    height: 165px;
    overflow: hidden;
    border-radius: 18px;
    margin: 0 auto;
}

.marketing-plan-template-overview-widget .preview {
    position: relative;
    cursor: pointer;
}
.marketing-plan-template-overview-widget .preview-overlay {
    position: absolute;
    display: flex; /* NEW, Spec - Firefox, Chrome, Opera */
    align-items: center;
    justify-content: center;
    z-index: 1;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    height: 100%;
    width: 100%;
    opacity: 0;
    background-color: rgba(89, 72, 110, 1);
    transition: opacity 500ms ease-out;
}

.marketing-plan-template-overview-widget .preview-overlay:hover {
    opacity: 0.8;
}

.marketing-plan-template-overview-widget .preview-overlay .preview-text {
    position: relative;
    font-size: 26px;
    font-weight: 600;
    color: #fff;
    z-index: 1;
}

.marketing-plan-template-overview-widget .eye-svg {
    width: 22.77px;
    top: 10px;
    right: 15px;
}
.marketing-plan-template-overview-widget .eye-svg path,
.marketing-plan-template-overview-widget .eye-svg circle {
    fill: #fff;
}

.marketing-plan-template-overview-widget .category-color {
    width: 10px;
    height: 10px;
    border-radius: 200px;
    margin-left: 10px;
    position: relative;
    top: 5px;
    margin-right: 8px;
}

.marketing-plan-template-overview-widget .widget-title-container {
    display: flex;
    align-items: center;
    justify-content: space-between;
}

.marketing-plan-template-overview-widget .j9-overview-svg {
    position: relative;
    margin-right: 28px;
}

.marketing-plan-template-overview-widget .campaign-name {
    font-size: 11px;
    line-height: 1em;
    color: #18aeff;
}

.marketing-plan-template-overview-widget .action-container {
    display: flex;
    justify-content: space-between;
}

.marketing-plan-template-overview-widget .action-container .edit-btn {
    margin-right: 10px;
    background: transparent;
    color: #18aeff;
}

.marketing-plan-template-overview-widget .action-container .edit-btn:hover {
    background: #e9e9ef;
}

.marketing-plan-template-overview-widget .agency-logo-container {
    display: flex;
    align-items: center;
    font-size: 12px;
}

.marketing-plan-template-overview-widget .agency-logo-container img {
    max-height: 30px;
    margin-left: 10px;
}
</style>
